<template>
  <van-swipe-cell class="comment-item-wrapper"
  :right-width="perm_delete ? 72 : 0.01"
  :left-width=0.1
  :on-close="onSwipeClose">
    <div class="comment-item" @click="handleReply">
      <div class="user-avatar">
        <badge v-if="unread" :id="`unread-${unreadIndex}`" :dotStyle="{'top': '12px', 'left': '-10px'}"/>
        <avatar
          :user="item.user"
          :size="30"
        />
      </div>
      <div class="comment-info">
        <div class="comment-header">
          <div class="username ellipsis">
            {{item.user.name}}
          </div>
          <div class="comment-time">{{actionTime}}</div>
        </div>
        <div class="comment-item-main">
          <div class="comment-content">
            <template v-if="item.is_voice">
              <audio ref="audio" :src="voiceUrl" preload="auto"></audio>
              <div :class="['voice-container', playState && ' voice-play']" @click.stop="handlePlayAudio">
                  <i :class="['iconfont', `icon-voice-${playState ? (voiceLevel === 0 ? 3 : voiceLevel) : '3'}`]"></i>
                  <span class="voice-length">{{item.voice_length}}''</span>
              </div>
            </template>
            <mentions-content
              v-else
              :content="item.content"
              :metionAllString="$t('file.comment.metionAllCollaborators')"
              :itemTypedId="itemTypedId"
            />
          </div>
        </div>
      </div>
    </div>
    <template v-if="perm_delete" slot="right">
      <div v-if="item.unread_count" class="mark-read" @click="recordActionName('mark')">{{$t('message.markAsRead')}}</div>
      <div class="delete" @click="recordActionName()">{{$t('base.delete')}}</div>
    </template>
    <span v-if="perm_delete" slot="right">{{$t('base.delete')}}</span>
  </van-swipe-cell>
</template>
<script>
import config from '../../config'
import ajax from '@/utils/ajax'
import {formatDate, confirm} from '@/utils/common'
import {i18n} from '@/utils/i18n'
import {COMMENTS_DELETE} from '@/config/api'
import Avatar from '../utils/Avatar'
import Badge from '../utils/Badge'
import MentionsContent from './MentionsContent'

export default {
  name: 'CommentItem',
  i18n,
  components: {
    Avatar,
    Badge,
    MentionsContent
  },
  props: {
    item: Object,
    unreadIndex: Number
  },
  data () {
    return {
      playState: false,
      unread: this.item.unread,
      voiceLevel: -1
    }
  },
  computed: {
    actionTime () {
      return formatDate(this.item.created_at * 1000)
    },
    itemTypedId () {
      const { item_type, item_id } = this.item
      return item_type && item_id && `${item_type}_${item_id}`
    },
    voiceUrl () {
      return `${config.imageUrl}/apps/comments/voice_download?comment_id=${this.item.id}&voice_format=mp3`
    },
    perm_delete () {
      return this.item.permissions && this.item.permissions.includes('delete_comment')
    }
  },
  watch: {
    'item.unread' (unread) {
      if (this.unread !== unread) {
        if (!unread) {
          if (!this.unreadTimer) {
            this.unreadTimer = setTimeout(() => {
              this.unread = unread
            }, 5000)
          }
        } else {
          this.unread = unread
        }
      }
    }
  },
  mounted () {
    if (this.$refs.audio) {
      this.$refs.audio.addEventListener('play', this.handlePlay)
      this.$refs.audio.addEventListener('pause', this.handleStop)
      this.$refs.audio.addEventListener('ended', this.handleStop)
    }
  },
  beforeDestroy () {
    this.isDestroyed = true
    this.voiceTimer && clearInterval(this.voiceTimer)
    this.unreadTimer && clearTimeout(this.unreadTimer)
    if (this.$refs.audio) {
      this.$refs.audio.removeEventListener('play', this.handlePlay)
      this.$refs.audio.removeEventListener('pause', this.handleStop)
      this.$refs.audio.removeEventListener('ended', this.handleStop)
    }
  },
  methods: {
    onSwipeClose (clickPosition, instance) {
      switch (clickPosition) {
        case 'left':
        case 'cell':
        case 'outside':
          instance.close()
          break
        case 'right':
          confirm({
            className: 'dialog danger-dialog',
            message: i18n.t('file.message.delete.confirm'),
            confirmButtonText: i18n.t('base.delete'),
            cancelButtonText: i18n.t('base.cancelText')
          }).then(() => {
            ajax({
              url: COMMENTS_DELETE,
              data: {
                comment_id: this.item.id
              }
            }).then(res => {
              if (res && res.success) {
                instance.close()
                this.$emit('comment-delete', this.item.id)
              }
            })
          }).catch(() => {}) // https://stackoverflow.com/questions/42460039/promise-reject-causes-uncaught-in-promise-warning
          break
      }
    },
    handlePlayAudio () {
      if (this.$refs.audio) {
        this.$emit('play-audio', this.$refs.audio)
      }
    },
    handlePlay () {
      if (this.voiceTimer) return
      this.voiceTimer = setInterval(() => {
        this.voiceLevel = (this.voiceLevel + 1) % 3
        this.playState = true
      }, 400)
    },
    handleStop () {
      clearInterval(this.voiceTimer)
      this.voiceTimer = null
      this.playState = false
      this.voiceLevel = -1
    },
    handleReply () {
      if (config.user_info.id !== this.item.user.id) {
        this.$emit('reply', this.item)
      }
    }
  }
}
</script>
<style lang="postcss">
.comment-item-wrapper {

  & .comment-item {
    display: flex;
    background-color: #fff;
    padding: 15px 0 0 0;

    & .user-avatar {
      position: relative;
      flex: 0 0 auto;
      width: 30px;
      margin: 0 10px 0 15px;
    }

    & .comment-info {
      flex: 1 1 auto;
      overflow: hidden;
      padding: 0 20px 15px 0;
      border-bottom: 1px solid #e5e7e9
    }

    & .username {
      height: 20px;
      line-height: 20px;
      font-size: 14px;
    }

    & .comment-time {
      height: 16px;
      line-height: 16px;
      font-size: 12px;
      color: #c1c1c1;
    }

    & .comment-content {
      font-size: 16px;
      line-height: 22px;
      margin-top: 8px;
    }

    & .voice-container {
      box-sizing: border-box;
      border: 1px solid #a4c6ff;
      width: 110px;
      height: 30px;
      line-height: 28px;
      position: relative;
      margin-left: 4px;
      cursor: pointer;
      background-color: #b1ceff;

      & .iconfont {
        color: #fff;
        margin-left: 8px;
        font-size: 12px;
      }

      & .voice-length {
        position: absolute;
        right: 10px;
        top: 0;
        font-size: 12px;
        color: #fff;
      }

      &:before,
      &:after {
        border-left: 5px solid transparent;
        border-top: 5px solid #a4c6ff;
        border-right: 5px solid transparent;
        content: ' ';
        position: absolute;
        z-index: 1;
        left: -5px;
        top: -1px
      }
    }
  }

  & .van-cell-swipe__right {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    background-color: #f55;
    color: #fff;
    font-size: 18px;
  }
}
</style>
